/**
 * Utility functions for detecting and classifying code/markup content
 */

/** Number of lines to show in code preview before collapsing */
export const CODE_BLOCK_PREVIEW_LINES = 3;

/**
 * Supported code languages for syntax highlighting.
 * Maps to Shiki language identifiers used by CodeBlock component.
 */
export type CodeLanguage = 'html' | 'json' | 'plain';

/**
 * Detect the language of code content for syntax highlighting.
 * Used to determine which Shiki language to use with CodeBlock.
 *
 * @param code - The code string to analyze
 * @returns The detected language: 'html', 'json', or 'plain'
 *
 * @example
 * detectCodeLanguage('<html><body>Hello</body></html>') // 'html'
 * detectCodeLanguage('{"key": "value"}') // 'json'
 * detectCodeLanguage('const x = 1;') // 'plain'
 */
export function detectCodeLanguage(code: string): CodeLanguage {
  const trimmed = code.trim();

  // Check for HTML - DOCTYPE declaration or HTML tags
  if (/<[A-Za-z][^>]*>/.test(trimmed) || /<!doctype/i.test(trimmed)) {
    return 'html';
  }

  // Check for JSON - starts/ends with braces or brackets and parses successfully
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) || (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      JSON.parse(trimmed);
      return 'json';
    } catch {
      // Not valid JSON, fall through to plain
    }
  }

  return 'plain';
}

/**
 * Check if a string value contains code, HTML, or template content
 * that needs special formatting (multiline display, syntax highlighting).
 *
 * @param value - The value to check
 * @returns true if value should be displayed as code/markup
 */
export function isCodeOrHtmlString(value: unknown): boolean {
  if (typeof value !== 'string') return false;

  const hasNewlines = value.includes('\n');
  const hasHtmlTags = /<[A-Za-z][^>]*>/.test(value);
  const hasTemplateSyntax = /{{.*}}|{%.*%}/.test(value);
  const isLongString = value.length > 200;

  return hasNewlines || hasHtmlTags || hasTemplateSyntax || isLongString;
}
